import classNames from 'classnames';
import type { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  BannersnackCreative,
  Campaign,
  DisplayCreative,
  FacebookDisplayCreative,
} from '@feathr/blackbox';
import { isAdTagCreative, isBannersnackCreative } from '@feathr/blackbox';
import {
  AdTagPreview,
  EmptyState,
  FacebookNewsfeedPreview,
  FacebookStoryPreview,
  InstagramFeedPreview,
  InstagramStoryPreview,
  Spinner,
  useVideoFrameExtract,
} from '@feathr/components';

import {
  getBannersnackPreviewUrl,
  getCreativeThumbnailUrl,
  hasStoredThumbnail,
} from '../../Creatives.utils';
import type { TSocialPreviewType } from '../SocialPreviewTypeSelector';
import SocialPreviewTypeSelector from '../SocialPreviewTypeSelector';
import ZoomControls from '../ZoomControls';
import { usePreviewPanel } from './PreviewPanel.hooks';
import { buildTestClickUrl, formatAdTag, getCtaLabel } from './PreviewPanel.utils';

import * as styles from './PreviewPanel.css';

export interface IPreviewPanelProps {
  /** The creative model to preview. */
  creative?: DisplayCreative;
  /**
   * Optional campaign for click-through tracking.
   * When provided, ad tag previews become interactive and clicks are tracked.
   */
  campaign?: Campaign;
  className?: string;
}

const PLACEHOLDER_PAGE_NAME = 'Your Page';
const PLACEHOLDER_HEADLINE = 'Headline';
const PLACEHOLDER_WEBSITE_URL = 'example.com';

function renderSocialPreview(
  socialPreviewType: TSocialPreviewType,
  imageUrl: string | undefined,
  videoUrl: string | undefined,
  creative: FacebookDisplayCreative,
  t: TFunction,
): JSX.Element {
  const mediaProps = videoUrl ? { videoUrl } : { imageUrl: imageUrl! };
  const primaryText = creative.get('primary_text') || undefined;
  const headline = creative.get('headlines')?.[0] || PLACEHOLDER_HEADLINE;
  const websiteUrl = creative.get('display_link') || PLACEHOLDER_WEBSITE_URL;
  const ctaText = getCtaLabel(creative.get('call_to_action_type'));

  const commonProps = {
    ...mediaProps,
    pageName: PLACEHOLDER_PAGE_NAME,
    primaryText,
    ctaText,
    t,
  };

  switch (socialPreviewType) {
    case 'facebook_story':
      return <FacebookStoryPreview {...commonProps} />;

    case 'facebook_newsfeed':
      return (
        <FacebookNewsfeedPreview {...commonProps} headline={headline} websiteUrl={websiteUrl} />
      );

    case 'instagram_feed':
      return <InstagramFeedPreview {...commonProps} />;

    case 'instagram_story':
      return <InstagramStoryPreview {...commonProps} />;
  }
}

function PreviewPanel({
  creative,
  campaign,
  className,
}: Readonly<IPreviewPanelProps>): JSX.Element {
  const { t } = useTranslation();
  const [videoPlaybackStarted, setVideoPlaybackStarted] = useState(false);

  // Reset video playback state when switching between creatives
  useEffect(() => {
    setVideoPlaybackStarted(false);
  }, [creative?.id]);

  const isAdTag = creative ? isAdTagCreative(creative) : false;
  const isBannersnack = creative ? isBannersnackCreative(creative) : false;
  const isMetaCreative = creative?.isFacebook;
  const isVideo = creative?.creativeType === 'Video';
  const contentWidth = creative?.get('width');
  const contentHeight = creative?.get('height');
  const creativeName = creative?.name;

  const testClickUrl = creative && campaign ? buildTestClickUrl(creative, campaign) : undefined;

  /*
   * For Bannersnack creatives, prefer the static image from banner_urls (more reliable).
   * Fall back to preview_adtag if banner_urls is empty (e.g., newly created/edited creative).
   */
  const bannersnackImageUrl =
    isBannersnack && creative
      ? getBannersnackPreviewUrl((creative as BannersnackCreative).get('banner_urls'))
      : undefined;
  const useBannersnackImage = isBannersnack && !!bannersnackImageUrl;
  const useBannersnackAdTag = isBannersnack && !bannersnackImageUrl;

  const hasThumbnail = creative ? hasStoredThumbnail(creative) : false;
  const creativeUrl =
    !isAdTag && !isBannersnack && creative ? getCreativeThumbnailUrl(creative) : undefined;
  const needsVideoExtract = isVideo && !hasThumbnail && !!creativeUrl;
  const {
    thumbnailUrl: videoFrameUrl,
    isLoading: isVideoThumbnailLoading,
    hasError: hasVideoThumbnailError,
  } = useVideoFrameExtract(creativeUrl ?? '', needsVideoExtract);

  const imageUrl = isVideo && !hasThumbnail ? videoFrameUrl || undefined : creativeUrl;
  const videoUrl = isVideo ? creative?.get('url') ?? undefined : undefined;

  const {
    zoom,
    socialPreviewType,
    viewportRef,
    contentRef,
    handleZoomChange,
    handleZoomToFit,
    handleSocialPreviewTypeChange,
  } = usePreviewPanel({ contentWidth, contentHeight });

  function renderPreview(): JSX.Element {
    const hasBannersnackPreview = useBannersnackImage || useBannersnackAdTag;
    if (
      (needsVideoExtract && hasVideoThumbnailError) ||
      (!imageUrl &&
        !videoUrl &&
        !(needsVideoExtract && isVideoThumbnailLoading) &&
        !isAdTag &&
        !hasBannersnackPreview)
    ) {
      return (
        <EmptyState
          description={t('Upload an image to see a preview here.')}
          label={t('No preview available')}
        />
      );
    }

    if (isAdTag || useBannersnackAdTag) {
      const html = isAdTag
        ? creative?.get('adtag') ?? ''
        : (creative as BannersnackCreative)?.get('preview_adtag') ?? '';
      const formattedHtml = testClickUrl ? formatAdTag(html, testClickUrl) : html;
      const width = creative?.get('width') ?? 300;
      const height = creative?.get('height') ?? 250;
      return (
        <AdTagPreview
          allowInteraction={!!testClickUrl}
          height={height}
          html={formattedHtml}
          // Key forces iframe remount when dimensions change (browsers cache srcDoc)
          key={`${width}x${height}`}
          scale={zoom / 100}
          width={width}
        />
      );
    }

    if (useBannersnackImage) {
      const bannersnackImage = (
        <img alt={creativeName} className={styles.image} src={bannersnackImageUrl} />
      );

      if (testClickUrl) {
        return (
          <a href={testClickUrl} rel={'noopener noreferrer'} target={'_blank'}>
            {bannersnackImage}
          </a>
        );
      }

      return bannersnackImage;
    }

    if (isMetaCreative && (imageUrl || videoUrl)) {
      return renderSocialPreview(
        socialPreviewType,
        imageUrl,
        videoUrl,
        creative as FacebookDisplayCreative,
        t,
      );
    }

    if (videoUrl) {
      const handleVideoPlay = (): void => setVideoPlaybackStarted(true);

      return (
        <div className={styles.videoContainer}>
          {videoPlaybackStarted && testClickUrl && (
            <a
              className={styles.videoClickZone}
              href={testClickUrl}
              rel={'noopener noreferrer'}
              target={'_blank'}
            >
              <span className={styles.hidden}>{t('Click to visit destination')}</span>
            </a>
          )}
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            className={styles.video}
            controls
            data-name={'video-preview'}
            onPlay={handleVideoPlay}
            src={videoUrl}
          />
        </div>
      );
    }

    const displayImage = (
      <>
        {needsVideoExtract && isVideoThumbnailLoading && <Spinner />}
        <img
          alt={creativeName}
          className={classNames(
            styles.image,
            needsVideoExtract && isVideoThumbnailLoading && styles.hidden,
          )}
          src={imageUrl}
        />
      </>
    );

    if (testClickUrl) {
      return (
        <a href={testClickUrl} rel={'noopener noreferrer'} target={'_blank'}>
          {displayImage}
        </a>
      );
    }

    return displayImage;
  }

  return (
    <div className={classNames(styles.root, className)}>
      <div className={styles.toolbar}>
        {isMetaCreative && (
          <SocialPreviewTypeSelector
            onChange={handleSocialPreviewTypeChange}
            value={socialPreviewType}
          />
        )}
        <ZoomControls
          className={styles.zoomControls}
          onZoomChange={handleZoomChange}
          onZoomToFit={handleZoomToFit}
          zoom={zoom}
        />
      </div>
      <div className={styles.viewport} ref={viewportRef}>
        {isAdTag ? (
          <div className={styles.adTagContainer} ref={contentRef}>
            {renderPreview()}
          </div>
        ) : (
          <div className={styles.content} ref={contentRef}>
            {renderPreview()}
          </div>
        )}
      </div>
    </div>
  );
}

export default observer(PreviewPanel);
